import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Pending,
  PhotoCamera,
  Upload,
  EmojiEvents,
  AttachMoney,
  Repeat,
  Edit,
  Delete
} from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useFamily } from '../contexts/FamilyContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { getRecurringDescription, getRecurringIcon } from '../utils/recurringTaskHelper';
import { fireConfetti, getMotivationalMessage, getCelebrationType } from '../utils/celebrationHelper';
import TaskTimer from './TaskTimer';

function TaskCard({ task, isParent = false }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editedTask, setEditedTask] = useState({});
  const { updateTask, deleteTask } = useFamily();
  const { familyId } = useFamilyId();

  // Safety check for invalid task data
  if (!task || !task.id) {
    console.warn('TaskCard: Invalid task data:', task);
    return null;
  }

  // Additional safety checks for task properties
  if (!task.title || task.title.trim() === '') {
    console.warn('TaskCard: Task missing title:', task);
    return null;
  }

  const handlePhotoUpload = async (file, type) => {
    if (!file) return null;

    try {
      const storageRef = ref(storage, `tasks/${task.id}/${type}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  };

  const handleCompleteTask = async () => {
    setError('');
    setUploading(true);

    try {
      const photos = {};

      // Handle photo requirements
      if (task.photoRequired === 'after' || task.photoRequired === 'both') {
        if (!afterPhoto) {
          setError('After photo is required');
          setUploading(false);
          return;
        }
        photos.after = await handlePhotoUpload(afterPhoto, 'after');
      }

      if (task.photoRequired === 'both') {
        if (!beforePhoto) {
          setError('Before photo is required');
          setUploading(false);
          return;
        }
        photos.before = await handlePhotoUpload(beforePhoto, 'before');
      }

      // Update task status
      await updateTask(task.id, {
        status: 'pending_approval',
        completedAt: new Date().toISOString(),
        photos: photos
      });

      // 🎉 CELEBRATION TIME!
      fireConfetti('task');
      
      // Show motivational message
      const message = getMotivationalMessage(1, 0);
      setTimeout(() => {
        alert(message);
      }, 500);

      setOpenDialog(false);
      setBeforePhoto(null);
      setAfterPhoto(null);
    } catch (error) {
      setError('Failed to complete task. Please try again.');
      console.error('Error completing task:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleApproveTask = async () => {
    try {
      await updateTask(task.id, {
        status: 'completed',
        approvedAt: new Date().toISOString()
      });

      // Delete associated screen time rule (unlockOnApprove: true)
      if (familyId) {
        try {
          const rulesRef = collection(db, `families/${familyId}/screenTimeRules`);
          const q = query(rulesRef, where('linkedChoreId', '==', task.id), where('unlockOnApprove', '==', true));
          const snap = await getDocs(q);
          for (const ruleDoc of snap.docs) {
            await deleteDoc(doc(db, `families/${familyId}/screenTimeRules/${ruleDoc.id}`));
          }
        } catch (ruleErr) {
          console.error('Failed to delete screen time rule:', ruleErr);
          // Don't fail the approval if rule cleanup fails
        }
      }

      // 🎊 BIG celebration for approved tasks!
      const celebrationType = getCelebrationType(1, 0);
      fireConfetti(celebrationType);
      
      setOpenDialog(false);
    } catch (error) {
      console.error('Error approving task:', error);
    }
  };

  const handleRejectTask = async () => {
    try {
      await updateTask(task.id, {
        status: 'pending',
        completedAt: null,
        photos: {}
      });
      setOpenDialog(false);
    } catch (error) {
      console.error('Error rejecting task:', error);
    }
  };

  const getStatusColor = () => {
    switch (task.status) {
      case 'completed': return 'success';
      case 'pending_approval': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (task.status) {
      case 'completed': return <CheckCircle />;
      case 'pending_approval': return <Pending />;
      default: return <EmojiEvents />;
    }
  };

  const handleOpenEdit = () => {
    setEditedTask({
      title: task.title,
      description: task.description,
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
      allowance: task.allowance || 0,
      isRecurring: task.isRecurring || false,
      recurringType: task.recurringType || 'daily',
      recurringInterval: task.recurringInterval || 1
    });
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setUploading(true);
    setError('');
    
    try {
      const updates = {
        title: editedTask.title,
        description: editedTask.description,
        dueDate: editedTask.dueDate ? new Date(editedTask.dueDate).toISOString() : task.dueDate,
        allowance: parseFloat(editedTask.allowance) || 0,
        isRecurring: editedTask.isRecurring,
        recurringType: editedTask.recurringType,
        recurringInterval: parseInt(editedTask.recurringInterval) || 1
      };

      await updateTask(task.id, updates);
      
      // Note: Calendar events are now handled by the native calendar system
      console.log('📅 Task updated - calendar events will be synced automatically');
      
      setOpenEditDialog(false);
      setEditedTask({});
    } catch (error) {
      setError('Failed to update task. Please try again.');
      console.error('Error updating task:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setUploading(true);
    setError('');
    
    try {
      // Note: Calendar events are now handled by the native calendar system
      console.log('📅 Task deleted - calendar events will be synced automatically');
      
      // Delete the task
      await deleteTask(task.id);
      setOpenDeleteDialog(false);
    } catch (error) {
      setError('Failed to delete task. Please try again.');
      console.error('Error deleting task:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="h6" component="h3">
              {task.title}
            </Typography>
            <Chip
              icon={getStatusIcon()}
              label={task.status?.replace('_', ' ').toUpperCase()}
              color={getStatusColor()}
              size="small"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            {task.description}
          </Typography>

          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {task.isRecurring && (
              <Chip
                icon={<Repeat />}
                label={`${getRecurringIcon(task.recurringType)} ${getRecurringDescription(task.recurringType, task.recurringInterval)}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {task.photoRequired !== 'none' && (
              <Chip
                icon={<PhotoCamera />}
                label={`Photo: ${task.photoRequired}`}
                size="small"
                color="info"
              />
            )}
            {task.allowance > 0 && (
              <Chip
                icon={<AttachMoney />}
                label={`$${task.allowance}`}
                size="small"
                color="success"
              />
            )}
            {task.dueDate && (
              <Chip
                label={`Due: ${new Date(task.dueDate).toLocaleDateString()}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>

        <CardActions sx={{ flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
          {!isParent && task.status === 'pending' && (
            <>
              <TaskTimer 
                task={task}
                compact={true}
                onComplete={(timeData) => {
                  console.log('Task completed with time:', timeData);
                  setOpenDialog(true);
                }}
              />
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={() => setOpenDialog(true)}
                fullWidth
              >
                Complete Task
              </Button>
            </>
          )}
          {isParent && task.status === 'pending_approval' && (
            <Button
              variant="outlined"
              onClick={() => setOpenDialog(true)}
              fullWidth
            >
              Review Submission
            </Button>
          )}
          {isParent && task.status !== 'completed' && (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleOpenEdit}
                fullWidth
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setOpenDeleteDialog(true)}
                fullWidth
              >
                Delete
              </Button>
            </Box>
          )}
          {task.status === 'completed' && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckCircle />}
              fullWidth
              disabled
            >
              Completed!
            </Button>
          )}
        </CardActions>
      </Card>

      {/* Task Completion Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isParent && task.status === 'pending_approval' ? 'Review Task' : 'Complete Task'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!isParent && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {task.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {task.description}
              </Typography>

              {(task.photoRequired === 'both' || task.photoRequired === 'before') && (
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Before Photo *
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Upload />}
                    fullWidth
                  >
                    {beforePhoto ? beforePhoto.name : 'Upload Before Photo'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => setBeforePhoto(e.target.files[0])}
                    />
                  </Button>
                </Box>
              )}

              {(task.photoRequired === 'both' || task.photoRequired === 'after') && (
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    After Photo *
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Upload />}
                    fullWidth
                  >
                    {afterPhoto ? afterPhoto.name : 'Upload After Photo'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => setAfterPhoto(e.target.files[0])}
                    />
                  </Button>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                You'll earn ${task.allowance} after parent approval!
              </Alert>
            </Box>
          )}

          {isParent && task.status === 'pending_approval' && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {task.title}
              </Typography>
              
              {task.photos && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {task.photos.before && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Before
                      </Typography>
                      <img
                        src={task.photos.before}
                        alt="Before"
                        style={{ width: '100%', borderRadius: 8 }}
                      />
                    </Grid>
                  )}
                  {task.photos.after && (
                    <Grid item xs={12} sm={task.photos.before ? 6 : 12}>
                      <Typography variant="subtitle2" gutterBottom>
                        After
                      </Typography>
                      <img
                        src={task.photos.after}
                        alt="After"
                        style={{ width: '100%', borderRadius: 8 }}
                      />
                    </Grid>
                  )}
                </Grid>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                Approve this task to award ${task.allowance} allowance
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          {!isParent && (
            <Button
              onClick={handleCompleteTask}
              variant="contained"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Submit'}
            </Button>
          )}
          {isParent && task.status === 'pending_approval' && (
            <>
              <Button onClick={handleRejectTask} color="error">
                Reject
              </Button>
              <Button onClick={handleApproveTask} variant="contained" color="success">
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Edit />
            Edit Task
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Title"
                value={editedTask.title || ''}
                onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                disabled={uploading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editedTask.description || ''}
                onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                disabled={uploading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={editedTask.dueDate || ''}
                onChange={(e) => setEditedTask({ ...editedTask, dueDate: e.target.value })}
                disabled={uploading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Allowance ($)"
                type="number"
                value={editedTask.allowance || 0}
                onChange={(e) => setEditedTask({ ...editedTask, allowance: e.target.value })}
                disabled={uploading}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editedTask.isRecurring || false}
                    onChange={(e) => setEditedTask({ ...editedTask, isRecurring: e.target.checked })}
                    disabled={uploading}
                  />
                }
                label="Recurring Task"
              />
            </Grid>
            
            {editedTask.isRecurring && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={uploading}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={editedTask.recurringType || 'daily'}
                      onChange={(e) => setEditedTask({ ...editedTask, recurringType: e.target.value })}
                      label="Frequency"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Repeat Every"
                    type="number"
                    value={editedTask.recurringInterval || 1}
                    onChange={(e) => setEditedTask({ ...editedTask, recurringInterval: e.target.value })}
                    disabled={uploading}
                    inputProps={{ min: 1 }}
                    helperText={`Every ${editedTask.recurringInterval || 1} ${editedTask.recurringType || 'day'}(s)`}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={uploading || !editedTask.title}
            startIcon={uploading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {uploading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm">
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} color="error.main">
            <Delete />
            Delete Task?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>"{task.title}"</strong>?
          </Typography>
          {task.calendarEventId && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will also delete the corresponding Google Calendar event.
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <Delete />}
          >
            {uploading ? 'Deleting...' : 'Delete Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TaskCard;
